// Submenu — Desktop panel controller
//
// Positions the panel relative to its trigger and handles close.
// Repositions on window resize while open.
//
// Values:
//   position: "auto" | "top" | "bottom" | "left" | "right"
//             | "top-left" | "top-right" | "bottom-left" | "bottom-right"
//
// Usage:
//   <desktop-panel data-controller="submenu--panel"
//     data-submenu--panel-position-value="bottom-right"
//     data-action="submenu:opened@window->submenu--panel#position">

import { Controller } from '@hotwired/stimulus'
import * as positioning from './positioning'

export default class extends Controller {
  static targets = ['panel']
  static values = { position: { type: String, default: 'auto' } }

  connect() {
    this.submenu = this.element.closest('sub-menu')
    this.boundResize = this.#onResize.bind(this)
    window.addEventListener("resize", this.boundResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.boundResize)
    cancelAnimationFrame(this.rafResize)
  }

  position(event) {
    if (event?.detail?.id && event.detail.id !== this.submenu.dataset.submenuId) return
    if (!this.hasPanelTarget) return

    const id = this.submenu.dataset.submenuId
    const trigger = document.getElementById(`${id}-trigger`)
    if (!trigger) return

    const t = trigger.getBoundingClientRect()
    const p = this.panelTarget.getBoundingClientRect()

    const { top, left } = this.positionValue === 'auto'
      ? positioning.auto(t, p)
      : positioning.resolve(this.positionValue, t, p)

    this.panelTarget.style.setProperty("--top", `${top}px`)
    this.panelTarget.style.setProperty("--left", `${left}px`)
  }

  close() {
    this.dispatch("requestClose", { prefix: "submenu", bubbles: true })
  }

  #onResize() {
    if (!this.submenu.hasAttribute("is-open")) return
    cancelAnimationFrame(this.rafResize)
    this.rafResize = requestAnimationFrame(() => this.position())
  }
}
